import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface CartSummaryItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

const PAYMENT_METHODS: PaymentMethod[] = [
  { id: "cash", name: "Efectivo", icon: "ri-money-dollar-circle-line", color: "bg-primary-500" },
  { id: "card", name: "Tarjeta", icon: "ri-bank-card-line", color: "bg-blue-500" },
  { id: "transfer", name: "Transferencia", icon: "ri-exchange-line", color: "bg-purple-500" },
  { id: "split", name: "Pago Dividido", icon: "ri-split-cells-horizontal", color: "bg-orange-500" },
];

const MOCK_CART: CartSummaryItem[] = [
  { id: "1", name: "Product A", price: 29.99, quantity: 2 },
  { id: "2", name: "Product B", price: 15.50, quantity: 1 },
  { id: "3", name: "Product C", price: 45.00, quantity: 3 },
];

export default function CheckoutSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [cashReceived, setCashReceived] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const subtotal = MOCK_CART.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const tax = subtotal * 0.16;
  const total = subtotal + tax;

  const cashReceivedNum = parseFloat(cashReceived) || 0;
  const change = cashReceivedNum - total;

  const quickCashAmounts = [50, 100, 200, 500, 1000];

  const handleProcessPayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      alert(t("posCheckout.paymentSuccess"));
    }, 2000);
  };

  return (
    <div className="h-full flex">
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          <h1 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>
            <i className="ri-wallet-3-line mr-2 text-primary-500"></i>
            {t("posCheckout.title")}
          </h1>

          <div className={`${themeClasses.cardBg} rounded-2xl p-6 border ${themeClasses.border} mb-6`}>
            <h2 className={`font-bold ${themeClasses.text} mb-4`}>{t("posCheckout.selectPaymentMethod")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedMethod === method.id
                      ? "border-primary-500 bg-primary-500/10"
                      : `${themeClasses.border} hover:border-primary-500/50`
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl ${method.color} flex items-center justify-center mb-3 mx-auto`}>
                    <i className={`${method.icon} text-2xl text-white`}></i>
                  </div>
                  <p className={`font-medium ${themeClasses.text} text-center`}>{method.name}</p>
                </button>
              ))}
            </div>
          </div>

          {selectedMethod === "cash" && (
            <div className={`${themeClasses.cardBg} rounded-2xl p-6 border ${themeClasses.border} mb-6`}>
              <h2 className={`font-bold ${themeClasses.text} mb-4`}>{t("posCheckout.cashPayment")}</h2>
              
              <div className="mb-4">
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posCheckout.amountReceived")}
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold ${themeClasses.text}`}>
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value.replace(/[^0-9.]/g, ""))}
                    placeholder="0.00"
                    className={`w-full pl-10 pr-4 py-4 text-3xl font-bold rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 text-center`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-5 gap-2 mb-6">
                {quickCashAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setCashReceived(amount.toString())}
                    className={`p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium hover:bg-primary-500 hover:text-gray-900 transition-colors`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              {cashReceivedNum >= total && (
                <div className={`p-4 rounded-xl ${change > 0 ? "bg-primary-500/10 border border-primary-500/30" : "bg-primary-500/10 border border-primary-500/30"}`}>
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${change > 0 ? "text-primary-500" : "text-primary-500"}`}>
                      {t("posCheckout.change")}
                    </span>
                    <span className={`text-2xl font-bold ${change > 0 ? "text-primary-500" : "text-primary-500"}`}>
                      ${change.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedMethod === "card" && (
            <div className={`${themeClasses.cardBg} rounded-2xl p-6 border ${themeClasses.border} mb-6`}>
              <h2 className={`font-bold ${themeClasses.text} mb-4`}>{t("posCheckout.cardPayment")}</h2>
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-20 h-20 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                  <i className="ri-bank-card-line text-4xl text-blue-500"></i>
                </div>
                <p className={`${themeClasses.text} font-medium mb-2`}>{t("posCheckout.waitingForCard")}</p>
                <p className={themeClasses.textSecondary}>{t("posCheckout.insertOrSwipeCard")}</p>
              </div>
            </div>
          )}

          {selectedMethod === "transfer" && (
            <div className={`${themeClasses.cardBg} rounded-2xl p-6 border ${themeClasses.border} mb-6`}>
              <h2 className={`font-bold ${themeClasses.text} mb-4`}>{t("posCheckout.transferPayment")}</h2>
              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${themeClasses.bg} border ${themeClasses.border}`}>
                  <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>{t("posCheckout.bankAccount")}</p>
                  <p className={`font-mono font-bold ${themeClasses.text}`}>1234 5678 9012 3456</p>
                </div>
                <div className={`p-4 rounded-xl ${themeClasses.bg} border ${themeClasses.border}`}>
                  <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>{t("posCheckout.clabe")}</p>
                  <p className={`font-mono font-bold ${themeClasses.text}`}>012345678901234567</p>
                </div>
                <div className={`p-4 rounded-xl ${themeClasses.bg} border ${themeClasses.border}`}>
                  <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>{t("posCheckout.reference")}</p>
                  <p className={`font-mono font-bold ${themeClasses.text}`}>TXN-{Date.now()}</p>
                </div>
              </div>
            </div>
          )}

          {selectedMethod === "split" && (
            <div className={`${themeClasses.cardBg} rounded-2xl p-6 border ${themeClasses.border} mb-6`}>
              <h2 className={`font-bold ${themeClasses.text} mb-4`}>{t("posCheckout.splitPayment")}</h2>
              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${themeClasses.bg} border ${themeClasses.border}`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className={themeClasses.text}>{t("posCheckout.payment")} 1 - {t("posCheckout.cash")}</span>
                    <input
                      type="text"
                      placeholder="$0.00"
                      className={`w-32 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} text-right focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={themeClasses.text}>{t("posCheckout.payment")} 2 - {t("posCheckout.card")}</span>
                    <input
                      type="text"
                      placeholder="$0.00"
                      className={`w-32 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} text-right focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    />
                  </div>
                </div>
                <button className={`w-full p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} border-dashed`}>
                  <i className="ri-add-line mr-2"></i>
                  {t("posCheckout.addPaymentMethod")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`w-80 lg:w-96 ${themeClasses.bg} border-l ${themeClasses.border} flex flex-col`}>
        <div className={`p-4 border-b ${themeClasses.border}`}>
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posCheckout.orderSummary")}</h2>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {MOCK_CART.length} {t("posCart.products")}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {MOCK_CART.map((item) => (
            <div
              key={item.id}
              className={`flex justify-between items-center py-2 border-b ${themeClasses.border}`}
            >
              <div>
                <p className={`font-medium ${themeClasses.text}`}>{item.name}</p>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  ${item.price.toFixed(2)} × {item.quantity}
                </p>
              </div>
              <span className={`font-bold ${themeClasses.text}`}>
                ${(item.price * item.quantity).toFixed(2)}
              </span>
            </div>
          ))}
        </div>

        <div className={`p-4 border-t ${themeClasses.border} space-y-3`}>
          <div className="flex justify-between">
            <span className={themeClasses.textSecondary}>{t("posTerminal.subtotal")}</span>
            <span className={themeClasses.text}>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className={themeClasses.textSecondary}>{t("posTerminal.tax")} (16%)</span>
            <span className={themeClasses.text}>${tax.toFixed(2)}</span>
          </div>
          <div className={`flex justify-between text-xl font-bold border-t ${themeClasses.border} pt-3`}>
            <span className={themeClasses.text}>{t("posTerminal.total")}</span>
            <span className="text-primary-500">${total.toFixed(2)}</span>
          </div>

          <button
            onClick={handleProcessPayment}
            disabled={!selectedMethod || isProcessing || (selectedMethod === "cash" && cashReceivedNum < total)}
            className="w-full p-4 rounded-xl bg-primary-500 text-gray-900 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                {t("posCheckout.processing")}
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                {t("posCheckout.completePayment")}
              </>
            )}
          </button>

          <button className={`w-full p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium`}>
            <i className="ri-arrow-left-line mr-2"></i>
            {t("posCheckout.backToCart")}
          </button>
        </div>
      </div>
    </div>
  );
}
